/** Tiny DOM helpers for the game UI (no framework — matches the template style). */

interface ElProps {
  onClick?: (e: MouseEvent) => void;
  dataset?: Record<string, string>;
  style?: string;
  // other DOM props (textContent, src, type, value, disabled, …) are set via Object.assign
  [key: string]: unknown;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = '',
  props: ElProps = {},
  ...children: (Node | string | null | undefined)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  const { onClick, dataset, style, ...rest } = props;
  // Handler keys are normalised to lowercase before assignment.
  //
  // Object.assign sets DOM properties, and those are all lowercase: `onchange`,
  // `oninput`, `onsubmit`. Passing `onChange` therefore created a JS expando that
  // looked wired, typechecked fine, and never fired — which is exactly how the
  // villager job and standing-order dropdowns silently did nothing, making the
  // whole task economy unreachable. Anything shaped like on* is lowercased so the
  // camelCase spelling works too, rather than failing in silence.
  for (const [k, v] of Object.entries(rest)) {
    if (/^on[A-Z]/.test(k) && typeof v === 'function') {
      delete (rest as Record<string, unknown>)[k];
      (rest as Record<string, unknown>)[k.toLowerCase()] = v;
    }
  }
  Object.assign(node, rest);
  if (style) node.setAttribute('style', style);
  if (dataset) for (const [k, v] of Object.entries(dataset)) node.dataset[k] = v;
  if (onClick) node.addEventListener('click', onClick as EventListener);
  for (const c of children) if (c != null) node.append(c);
  return node;
}

/**
 * Push a fetched blob at the browser as a download without leaking the URL.
 *
 * Two things have to hold at once here.
 *
 * The anchor IS attached before it is clicked, and removed straight after. A
 * detached `<a download>` is the one variant browsers have historically ignored
 * (Safari and iOS in particular), and this game is played on mobile web — a
 * download that silently does nothing is worse than the bug below.
 *
 * The click must nonetheless never reach `document`. main.ts installs a
 * document-level handler that routes same-origin links in-app, and a
 * `blob:http://host/<uuid>` URL reports the PAGE's origin — so an attached
 * download anchor sailed past that handler's origin guard, got preventDefault()'d
 * and navigated the SPA to /<uuid>, which falls through the router to /play. That
 * full remount is what players reported as "Statement PDF reloads the game".
 * Stopping propagation on the anchor itself keeps the browser's own default
 * download action (the target-phase listener runs, the default is untouched)
 * while the router never sees the event at all.
 */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = el('a', '', { href: url, download: filename, rel: 'noopener', style: 'display:none' });
  a.addEventListener('click', (e) => e.stopPropagation());
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function clear(node: HTMLElement) {
  node.replaceChildren();
}

// Emoji get stripped from toasts (icon-only design; toasts are plain text). Keeps
// arrows (→) and other non-emoji symbols.
const TOAST_EMOJI_RE = /(?:\p{Extended_Pictographic}|[★☆✓✔✗✘✕✖✎])️?/gu;

/**
 * Toasts: deduplicated, capped, and stacked.
 *
 * Every toast used to be an independent node pinned to the SAME coordinates
 * (`bottom-24 left-1/2`) for 2.6 seconds, with no dedupe and no ceiling. So a
 * player mashing E next to no horse got ninety copies of "You have no horse"
 * layered on one another — an unreadable smear rather than a message, plus ninety
 * live DOM nodes. With 186 toast call sites across the engine, that was the whole
 * game's notification behaviour, not one button's.
 *
 * Three rules now, the same three the sound limiter uses for the same reason
 * (see audio/limiter.ts, "ten orcs sound like ten copies of one file"):
 *   1. REPEAT rather than restack — an identical message that is still on screen
 *      becomes "…  x3" and its timer resets. The count matters: silently dropping
 *      the repeat makes the game look deaf to the input.
 *   2. CAP the number visible. Past a few, they stop being read at all.
 *   3. STACK them in a column, so two DIFFERENT messages never occupy one pixel.
 */
const TOAST_MAX = 3;
const TOAST_MS = 2600;

interface LiveToast {
  node: HTMLElement;
  label: HTMLElement;
  text: string;
  kind: 'ok' | 'error';
  count: number;
  timer: number;
}
const liveToasts: LiveToast[] = [];
let toastStack: HTMLElement | null = null;

/** The column every toast lives in. Created lazily so importing this is free. */
function stack(): HTMLElement {
  if (toastStack?.isConnected) return toastStack;
  toastStack = el('div',
    // bottom-24 keeps the column clear of the farm hotbar (absolute bottom-3).
    // `pointer-events-none` so a toast can never swallow a tap meant for the world
    // underneath it — they are feedback, not controls.
    //
    // left-0 right-0 rather than left-1/2 + translate: the column is now what
    // gives the toasts their width, so it has to HAVE one. `items-center` still
    // centres them, and dropping the transform also stops this element being the
    // containing block for anything positioned inside it.
    'fixed bottom-24 left-0 right-0 z-50 flex flex-col items-center gap-1.5 pointer-events-none',
    // The safe-area inset on top of that 6rem clearance, so a notched phone's
    // home-indicator gesture bar never sits under the column.
    { style: 'bottom: max(6rem, calc(6rem + env(safe-area-inset-bottom)))' });
  document.body.appendChild(toastStack);
  return toastStack;
}

function dropToast(t: LiveToast) {
  window.clearTimeout(t.timer);
  t.node.remove();
  const i = liveToasts.indexOf(t);
  if (i >= 0) liveToasts.splice(i, 1);
}

/** A lightweight toast for action feedback / errors. */
export function toast(message: string, kind: 'ok' | 'error' = 'ok') {
  const clean = message.replace(TOAST_EMOJI_RE, '').replace(/\s{2,}/g, ' ').trim();

  // Same words, still on screen → count it instead of stacking a duplicate.
  const dupe = liveToasts.find((t) => t.text === clean && t.kind === kind);
  if (dupe) {
    dupe.count += 1;
    dupe.label.textContent = `${clean}  x${dupe.count}`;
    window.clearTimeout(dupe.timer);
    dupe.timer = window.setTimeout(() => dropToast(dupe), TOAST_MS);
    return;
  }

  // Oldest first: a burst of different messages should leave the NEWEST visible,
  // because that is the one describing what just happened.
  while (liveToasts.length >= TOAST_MAX) dropToast(liveToasts[0]);

  const label = el('span', '', {}, clean);
  const node = el(
    'div',
    // NOT positioned. `stack()` above already places and centres the column —
    // this used to repeat `fixed bottom-24 left-1/2 -translate-x-1/2` here, and
    // the repetition is what made every long toast a sliver of wrapped text:
    // the stack carries `translate: -50%`, a transform, which makes it the
    // containing block for a `position: fixed` CHILD. The stack is shrink-to-fit
    // and its only child was out of flow, so it measured 0px wide — and the toast
    // resolved its own width against that nothing and collapsed to its longest
    // word. Measured at a 1600px viewport: 121x182 (eight lines) before, 660x42
    // (one line) after. A plain in-flow child of the centred column is all this
    // ever needed to be.
    //
    // max-w-[min(90vw,44rem)] is the only width rule left: keep a long sentence
    // off the screen edges on a phone, and off a 1600px-wide single line on a
    // desktop.
    `max-w-[min(90vw,44rem)] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lift ` +
      (kind === 'ok' ? 'bg-surface text-leaf border border-border' : 'bg-surface text-error border border-error'),
    {},
    label,
  );
  stack().appendChild(node);
  const live: LiveToast = { node, label, text: clean, kind, count: 1, timer: 0 };
  live.timer = window.setTimeout(() => dropToast(live), TOAST_MS);
  liveToasts.push(live);
}

/**
 * Say something only when the answer CHANGES.
 *
 * A cooldown is the wrong tool for a refusal. Standing still next to nothing, the
 * game has no new information at 300ms, at 700ms, or ever — "Too far from your
 * horse" is true the whole time and worth saying exactly once. This remembers the
 * last thing said for a given `key` and stays quiet until it differs; pass `null`
 * to clear the memory when the situation resolves, so the message can be said
 * again the next time it genuinely becomes true.
 */
const lastSaid = new Map<string, string>();
export function notifyOnce(key: string, message: string | null, kind: 'ok' | 'error' = 'ok') {
  if (message === null) { lastSaid.delete(key); return; }
  if (lastSaid.get(key) === message) return;
  lastSaid.set(key, message);
  toast(message, kind);
}

/** Test seam: forget every live toast and every remembered refusal. */
export function resetToastsForTest() {
  for (const t of [...liveToasts]) dropToast(t);
  lastSaid.clear();
  toastStack?.remove();
  toastStack = null;
}

/** Format ms as m:ss remaining. */
export function fmtRemaining(ms: number): string {
  const s = Math.ceil(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
